import {useXRInputSourceStates, useXRInputSourceState, XRControllerState, XRSpace, DefaultXRControllerOptions, useXRInputSourceStateContext, XRControllerModel, DefaultXRInputSourceRayPointerOptions, CombinedPointer, DefaultXRControllerGrabPointer, DefaultXRInputSourceRayPointer, XRSpaceType, useApplyXRSpaceMatrix, useXRSpace} from "@react-three/xr"
import {useFrame, useThree} from "@react-three/fiber";
import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as THREE from "three";
import {RunInAction} from "mobx-graphlink";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";
import {store} from "../../../../Store/index.js";
import {useXREvent_fixed} from "../../../../Utils/ThreeJS/XR/ControllerUtils.js";
import {IMAGE_FRAME_JUMP_DURATION, RCEffectPlayRequest, Right_InteractionMode_values} from "../../../../Store/main/tools/vr.js";
import {useButtonStateChangeListener, useStickDirectionalStateChangeListener} from "../../../../Utils/ThreeJS/XR/XRHooks.js";
import {GetSelectedEngineConfig} from "../../../../Server/DB/engineConfigs.js";
import {Remote_ConceptLinkVoiceNextSequence, Remote_MemoryPalaceNewPrompt, Remote_NotifyUserMarkedImageFrameAsLie, Remote_NotifyUserMarkedImageFrameAsMistagged, Remote_RequestTTS} from "../../../../Utils/Bridge/Bridge_RemoteUserProcesses.js";
import {GetControllerSide_Final, VRAction} from "../../../../Store/main/settings/vr.js";
import {getControllerPosAndDir} from "../../../../Utils/ThreeJS/XR/Controller.js";
import {WaitXThenRun} from "js-vextensions";
import {GetCurrentFrameFromVideoPlayer, GetLastFrameFromVideoBlob} from "../../../../Utils/UI/CanvasUtils.js";
import {memoryPalaceFrameDB} from "../../../../Utils/IndexedDB/MemoryPalaceFrameDB.js";
import {AssertNotify, RunInAction_Set} from "web-vcore";
import {MemoryPalacePromptInfo} from "../../../../Store/main/tools/vr/ImageFrame.js";

export const SceneXREventListeners = observer_mgl_r3(()=>{
	const vrState = store.main.tools.vr;
	const vrSettings = store.main.settings.vr;
	const config_full = GetSelectedEngineConfig();
	const config = config_full?.vr;

	useStickDirectionalStateChangeListener(useCallback((side, direction, state)=>{
		// hotkey: "change interaction mode"
		if (side == GetControllerSide_Final("right") && (direction == "left" || direction == "right") && state == "on") {
			RunInAction("SceneXREventListeners.stickTilted", ()=>{
				const oldIndex = Right_InteractionMode_values.indexOf(vrState.right_interactionMode);
				const newIndex = (oldIndex + (direction == "right" ? 1 : -1)).WrapToRange(0, Right_InteractionMode_values.length);
				vrState.right_interactionMode = Right_InteractionMode_values[newIndex];
				console.log("Right_InteractionMode:", vrState.right_interactionMode);
			});
		}

		// hotkey: "increase/decrease snapshot reveal-level"
		if (side == GetControllerSide_Final("right") && (direction == "up" || direction == "down") && state == "on") {
			RunInAction("SceneXREventListeners.stickTilted", ()=>{
				vrState.snapshotRevealLevel = (vrState.snapshotRevealLevel + (direction == "up" ? 1 : -1)).KeepBetween(0, 1);

				const activeFrame = config ? vrState.GetActiveImageFrame(config) : null;
				const promptInfo = activeFrame?.currentMemoryPromptInfo;
				if (promptInfo && promptInfo.time_doneWithDecodeAttempt == null) {
					RunInAction("SceneXREventListeners.stickTilted", ()=>promptInfo.time_doneWithDecodeAttempt = Date.now());
				}
			});
		}
	}, [vrState]));

	/*useXREvent_fixed("squeezestart", useCallback(e=>{
		if (e.data.handedness === GetControllerSide_Final("right")) {
			RunInAction("PaintController.squeezestart", ()=>{
				uiState.right_interactionMode = Right_InteractionMode_values[(Right_InteractionMode_values.indexOf(uiState.right_interactionMode) + 1) % Right_InteractionMode_values.length];
				console.log("Right_InteractionMode:", uiState.right_interactionMode);
			});
		}
	}, []));*/

	const rightController = useXRInputSourceState("controller", GetControllerSide_Final("right"))!;

	// detect when user presses controller hot-keys
	useButtonStateChangeListener(useCallback((side, button, state)=>{
		// hotkey: "show/hide ui"
		if (side == GetControllerSide_Final("right") && button == "stick" && state == "on") {
			RunInAction("SceneXREventListeners.stickPressed", ()=>vrState.showUI = !vrState.showUI);
		}

		// hotkey: "replace current image-frame with new one, in-place"
		if (side == GetControllerSide_Final("right") && button == "a" && state == "on") {
			// if there is a jump already in progress (and its effects are not yet applied), apply its effects immediately
			if (vrState.imageFrames_lastJumpStartInfo?.timerId != null) vrState.imageFrames_lastJumpStartInfo.ApplyEffectsIfNotYet();

			const jumpStartInfo = {time: Date.now(), timerId: null as number|n, ApplyEffectsIfNotYet() {
				if (jumpStartInfo.timerId == null) return; // if timer already completed / applied its effects, do nothing
				jumpStartInfo.timerId = null; // clear timer-id (this signifies that the timer has completed / applied its effects)
				if (config == null) return;
				RunInAction("SceneXREventListeners.jumpToNextImageFrame.waitDone", ()=>vrState.imageFrameOffset += config.frames_duration);
			}};
			jumpStartInfo.timerId = WaitXThenRun(IMAGE_FRAME_JUMP_DURATION, ()=>jumpStartInfo.ApplyEffectsIfNotYet());
			RunInAction("SceneXREventListeners.jumpToNextImageFrame", ()=>vrState.imageFrames_lastJumpStartInfo = jumpStartInfo);
		}

		if (side == GetControllerSide_Final("right") && button == "b" && state == "on") {
			DoVRAction(vrSettings.bButtonAction);
		}

		async function DoVRAction(action: VRAction) {
			// hotkey: "voice next concept-link dream-event-sequence"
			if (action == VRAction.conceptLinkNextSequence) {
				Remote_ConceptLinkVoiceNextSequence(vrSettings.conceptLink_ttsVolumeMultiplier);
			}
			// action: "mark active image-frame as a lie" + "trigger play of new rc-effect"
			else if (action == VRAction.markFrameLieAndPlayRCEffect && config) {
				const activeFrame = vrState.GetActiveImageFrame(config);
				if (activeFrame) {
					// if frame not yet marked as lie by user, mark it as lie
					if (!activeFrame.userMarkedAsLie) {
						activeFrame.userMarkedAsLie = true;
						Remote_NotifyUserMarkedImageFrameAsLie(activeFrame.lie, activeFrame.texturePath);
					}
					// else, IF code thinks frame is true (ie. voiced char is in image), then interpret this "second attempt to mark as lie" as being a "mark as mistagged" action (for user to press twice, we assume they heard a "false accuse" sound from the above, and want to correct that)
					// (for case where code voiced the char intentionally as a lie, and user wants to say "actually that char is indeed present": that case is too rare [since eg. unlikely for user to even detect it], to where accidental mis-double-clicks would outnumber it -- so we don't support it)
					/*else if (!activeFrame.lie) {
						Remote_NotifyUserMarkedImageFrameAsMistagged(activeFrame.texturePath);
					}*/
				}

				//RunInAction("SceneXREventListeners.playRCEffect", ()=>{
				const rightControllerInfo = getControllerPosAndDir(rightController);
				if (rightControllerInfo.pos == null || rightControllerInfo.dir == null) return;
				store.main.tools.vr.rcEffects_playRequestsToProcess.push(new RCEffectPlayRequest({
					time: Date.now(),
					rightControllerPosition: rightControllerInfo.pos,
					rightControllerDirection: rightControllerInfo.dir,
				}));
			} else if (action == VRAction.memoryPalacePrompt) {
				const activeFrame = config ? vrState.GetActiveImageFrame(config) : null;

				// special behavior: if reveal-level is already at max, then pressing button notifies that decode failed
				if (vrState.snapshotRevealLevel >= 1) {
					const promptInfo = activeFrame?.currentMemoryPromptInfo;
					if (promptInfo && promptInfo.time_decodeMarkedAsFailed == null) {
						RunInAction("SceneXREventListeners", ()=>promptInfo.time_decodeMarkedAsFailed = Date.now());
						Remote_RequestTTS(`Failed ${promptInfo.target}`, config_full?.memoryPalace.voice_volumeMultiplier ?? 1);
					}
					return;
				}

				vr_memoryPalacePromptLastRequestedAt = Date.now();
				const promptInfo = await Remote_MemoryPalaceNewPrompt();
				const targetKnownAndFrameActive = promptInfo != null && activeFrame != null;
				if (!targetKnownAndFrameActive) return; // must know target and have active-frame to proceed

				// first, register target within frame structure (so that UI can start trying to show the last X snapshots for that target)
				RunInAction("SceneXREventListeners.memoryPalacePrompt", ()=>{
					activeFrame.currentMemoryPromptInfo = new MemoryPalacePromptInfo({
						actionType: promptInfo!.actionType,
						target: promptInfo!.target,
						time_decodeStart: Date.now(),
					});
				});

				// if active frame is a video, then store a snapshot for that target
				if (activeFrame.frameData_videoClip) {
					const scale = .3; // rescale to 30% (save db-space, and reduce stutter on load from db)
					let lastFrameDataStr: string|n;
					const clearLastFrameDataStrIfInvalid = ()=>{
						if (lastFrameDataStr == null) return;
						if (lastFrameDataStr.length <= "data:,".length) lastFrameDataStr = null;
					};

					// first, try to get snapshot directly from the displayed video-player (if available)
					const videoPlayer = activeFrame.frameData_videoClip_player;
					if (lastFrameDataStr == null && videoPlayer && videoPlayer.paused && videoPlayer.currentTime >= videoPlayer.duration - 0.5) {
						lastFrameDataStr = GetCurrentFrameFromVideoPlayer(videoPlayer, scale);
						clearLastFrameDataStrIfInvalid();
					}
					// if that fails, try to get snapshot from the video-blob, by spawning a new, invisible video-player
					const videoBlob = activeFrame.frameData_videoClip?.blob;
					if (lastFrameDataStr == null && videoBlob) {
						lastFrameDataStr = await GetLastFrameFromVideoBlob(videoBlob, scale);
						clearLastFrameDataStrIfInvalid();
					}

					if (lastFrameDataStr != null) {
						await memoryPalaceFrameDB.StoreFrame({imageData: lastFrameDataStr, target: promptInfo.target});
					} else {
						AssertNotify(false, `Failed to get last-frame from video-blob for memory-palace snapshot. @videoBlobLength:${videoBlob?.size}`);
					}
				}
			}
		}
	}, [config, rightController, vrSettings.bButtonAction, vrSettings.conceptLink_ttsVolumeMultiplier, vrState]));

	return null;
});
export let vr_memoryPalacePromptLastRequestedAt = 0;